import {ApiError} from './errors';
import type {
  ApiErrorBody,
  Booking,
  Branding,
  ChannelMenu,
  CheckoutSession,
  CreateCheckoutSessionRequest,
  CreatePublicBookingRequest,
  CreatePublicBookingResponse,
  Feedback,
  FeedbackSubmit,
  GuestSession,
  OpenGuestSessionRequest,
  Order,
  ServiceStatus,
  AppendOrderEventsResponse,
  NewOrderEvent,
  SubmitOrderRequest,
} from './types';

/**
 * The guest API client.
 *
 * ## The channel is not a parameter
 *
 * A guest credential may only ever project `guest_qr`, and the server forces it.
 * There is deliberately **no channel argument anywhere in this file** — a guest
 * who can name a channel can ask for the POS menu, which is how a hidden item
 * leaks. `CHANNEL` below is a constant for exactly that reason.
 *
 * ## What this client may call
 *
 * The five operations the guest surface is built on, plus two the eleven screens
 * cannot be built without. Both extras are contract-sanctioned, not invented:
 *
 *   POST /v1/guest/sessions        openGuestSession   — the five
 *   GET  /v1/guest/session         getGuestSession
 *   GET  /v1/guest/stream          openGuestStream    (see ./stream.ts)
 *   GET  /v1/menu/projection       getMenuProjection
 *   POST /v1/orders                submitOrder
 *
 *   GET  /v1/orders/{order_id}     getOrder           — extension, see below
 *   GET  /v1/location/service-status  getServiceStatus — extension, see below
 *
 *   POST /v1/orders/{id}/checkout-sessions  createCheckoutSession — see below
 *   GET  /v1/checkout-sessions/{id}         getCheckoutSession
 *
 *   POST /v1/public/feedback       submitPublicFeedback — see below
 *
 * `submitPublicFeedback` names `guestSessionToken` in its own `security` block —
 * `public` in the path is the route being un-gated from the paid order-pay
 * feature, not an anonymous call — so it goes out with the guest credential like
 * every other write here.
 *
 * `getOrder` lists `guestSessionToken` in its own `security` block, and Q14
 * (order status) and Q17 (confirmation surviving a refresh) are unbuildable
 * without it: the stream's own description says a pushed frame is "a hint to
 * re-read", and this is the read.
 *
 * `getServiceStatus` is the read Q20 exists for, and its description says in as
 * many words that it is "readable by any credential, guest phones included".
 * **Its `security` block does not say that** — it inherits the document-level
 * `deviceAccessToken` and lists no guest override. That is a contract
 * inconsistency and it is flagged, not patched: this client calls it and treats
 * 401/403/501 as "unknown", so a guest never sees a spinner over a question the
 * server declined to answer.
 *
 * Both checkout operations name `guestSessionToken` in their own `security`
 * blocks and stopped being placeholders on 2026-08-11, when the hosted checkout
 * was driven end to end against ReservePay's sandbox. This file said "they are
 * absent here on purpose, do not add them" until 2026-08-12; that sentence was
 * true and is not any more.
 *
 * ## Every header this file sends must be on the API's CORS allow-list
 *
 * `POKPOK_CORS_ALLOWED_ORIGINS` defaults to EMPTY and `*` is deliberately not
 * the fallback for an API one hop from a payment. Worse, **a preflight that
 * omits a requested header still answers `200`** and the browser then declines
 * to send the real request — one green `OPTIONS`, no `POST`, a guest shown
 * "เน็ตไม่ติด", and a server log full of successes. The allow-list in
 * `crates/api/src/routes/mod.rs` is read off *this file*, so adding a header
 * here without adding it there is a defect that only appears in a browser.
 *
 * The checkout calls below add **no new header**: `Authorization`,
 * `Content-Type`, `Idempotency-Key` and `X-Pokpok-Tenant-Id` are already on the
 * list, and `Accept` is CORS-safelisted.
 */

/** Forced. Not a parameter. See the note above. */
const CHANNEL = 'guest_qr' as const;

export interface ApiClientOptions {
  /**
   * Base URL, from configuration.
   *
   * The contract is explicit: "clients must take the base URL from
   * configuration and never hard-code it." The `run.app` hostnames in the
   * `servers` block are correct today and are not a commitment.
   */
  baseUrl: string;
  /** The current guest session token, or null before a QR has been scanned. */
  getToken: () => string | null;
  /**
   * The tenant, when a session has established one. Sent as `X-Pokpok-Tenant-Id`
   * — optional defence in depth that never grants access. It turns a misrouted
   * request into a loud 403 instead of a quiet cross-tenant read.
   */
  getTenantId?: () => string | null;
  fetchImpl?: typeof fetch;
  /** Milliseconds before a request is abandoned. A hung socket on 4G is common. */
  timeoutMs?: number;
}

interface RequestOptions {
  method?: 'GET' | 'POST';
  path: string;
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  /** Required by the contract on every POST here. Mint once per intent, reuse on retry. */
  idempotencyKey?: string;
  /** Conditional GET. A 304 is returned as `notModified`, not as an error. */
  ifNoneMatch?: string | null;
  /**
   * Optimistic concurrency, as the order's `seq` last read.
   *
   * Required on any order event whose effect does not commute — `bill_requested`
   * is one, `line_item_added` and `staff_called` are not. A stale value is a
   * `412` carrying the current order, which is the client's cue to re-read
   * rather than to retry.
   */
  ifMatch?: string | null;
  /** Omit the Authorization header. Only `POST /v1/guest/sessions` does this. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  etag: string | null;
  /** `Idempotency-Replayed: true` — the server returned the original response. */
  replayed: boolean;
}

export interface ConditionalResponse<T> {
  /** Null when the server answered 304 and the caller should keep what it has. */
  data: T | null;
  notModified: boolean;
  etag: string | null;
}

export class ApiClient {
  readonly baseUrl: string;
  private readonly getToken: () => string | null;
  private readonly getTenantId: () => string | null;
  private readonly fetchImpl: typeof fetch;
  private readonly timeoutMs: number;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.getToken = options.getToken;
    this.getTenantId = options.getTenantId ?? (() => null);
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.timeoutMs = options.timeoutMs ?? 15_000;
  }

  /** Absolute URL for a contract path. `openGuestStream` needs this too. */
  url(path: string, query?: RequestOptions['query']): string {
    const url = new URL(this.baseUrl + path);
    for (const [key, value] of Object.entries(query ?? {})) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
    return url.toString();
  }

  /** Headers every authenticated guest request carries. `openGuestStream` reuses this. */
  authHeaders(anonymous = false): Record<string, string> {
    const headers: Record<string, string> = {};
    if (!anonymous) {
      const token = this.getToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }
    const tenantId = this.getTenantId();
    if (tenantId) headers['X-Pokpok-Tenant-Id'] = tenantId;
    return headers;
  }

  private async request<T>(options: RequestOptions): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...this.authHeaders(options.anonymous),
    };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;
    if (options.ifNoneMatch) headers['If-None-Match'] = options.ifNoneMatch;
    if (options.ifMatch) headers['If-Match'] = options.ifMatch;

    // A caller-supplied signal and the timeout both have to be able to abort.
    const timeout = AbortSignal.timeout(this.timeoutMs);
    const signal = options.signal
      ? AbortSignal.any([options.signal, timeout])
      : timeout;

    let response: Response;
    try {
      response = await this.fetchImpl(this.url(options.path, options.query), {
        method: options.method ?? 'GET',
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal,
        // No cookies anywhere on this surface. The guest credential is a bearer
        // token in a header; there is no session cookie to leak or to consent to.
        credentials: 'omit',
      });
    } catch (cause) {
      if (options.signal?.aborted) throw cause;
      throw ApiError.network(cause);
    }

    const etag = response.headers.get('ETag');
    const replayed = response.headers.get('Idempotency-Replayed') === 'true';

    if (response.status === 304) {
      return {data: null as T, status: 304, etag, replayed};
    }

    if (response.status === 204) {
      return {data: undefined as T, status: 204, etag, replayed};
    }

    const text = await response.text().catch(() => '');

    if (!response.ok) {
      const body = safeParse<ApiErrorBody>(text);
      if (body && typeof body === 'object' && 'error' in body && body.error?.code) {
        throw ApiError.fromBody(response.status, body);
      }
      // A non-contract error body (a proxy's 502 HTML, a gateway timeout page).
      // 5xx is worth retrying; a malformed 4xx is not.
      throw new ApiError({
        kind: 'parse',
        message: `Unreadable error response (${response.status})`,
        status: response.status,
        retriable: response.status >= 500,
      });
    }

    const data = safeParse<T>(text);
    if (data === undefined) {
      throw new ApiError({
        kind: 'parse',
        message: 'Response was not valid JSON',
        status: response.status,
        retriable: false,
      });
    }

    return {data, status: response.status, etag, replayed};
  }

  // --- the guest surface ---------------------------------------------------

  /**
   * `POST /v1/guest/sessions` — turn a scanned table QR into a session.
   *
   * Unauthenticated by design (`security: []`): the QR token *is* the
   * credential. `tenant_id` is optional in the schema and required in practice —
   * omit it and the scan is refused with a message naming the field (D-019).
   *
   * PDPA: no name, phone or email is required to order, and none is sent here.
   */
  async openGuestSession(
    request: OpenGuestSessionRequest,
    idempotencyKey: string,
    signal?: AbortSignal,
  ): Promise<GuestSession> {
    const {data} = await this.request<GuestSession>({
      method: 'POST',
      path: '/v1/guest/sessions',
      body: request,
      idempotencyKey,
      anonymous: true,
      signal,
    });
    return data;
  }

  /** `GET /v1/guest/session` — re-read the current session (refresh, resume, expiry check). */
  async getGuestSession(signal?: AbortSignal): Promise<GuestSession> {
    const {data} = await this.request<GuestSession>({
      path: '/v1/guest/session',
      signal,
    });
    return data;
  }

  /**
   * `GET /v1/menu/projection` — the one-menu projection, forced to `guest_qr`.
   *
   * `include_unavailable` defaults to false and stays false here: the POS greys
   * out an 86'd item so staff can un-86 it in place, but a guest has no such
   * action and the channel's own policy decides whether a sold-out item is
   * hidden or shown. Live 86s during a session arrive on the stream, not here.
   *
   * Pass the previous `ETag` to make this conditional — the whole menu over 4G
   * every time a guest returns to the list is the perf budget spent on nothing.
   */
  async getMenuProjection(
    options: {ifNoneMatch?: string | null; signal?: AbortSignal} = {},
  ): Promise<ConditionalResponse<ChannelMenu>> {
    const {data, status, etag} = await this.request<ChannelMenu>({
      path: '/v1/menu/projection',
      query: {channel: CHANNEL},
      ifNoneMatch: options.ifNoneMatch ?? null,
      signal: options.signal,
    });
    return {data: status === 304 ? null : data, notModified: status === 304, etag};
  }

  /**
   * `POST /v1/orders` — submit. Idempotent and offline-safe.
   *
   * Three layers protect against a duplicate order and a caller must respect all
   * three: a client-minted `order_id` kept forever, one `Idempotency-Key` per
   * intent reused across every attempt, and client-minted `line_id`s.
   *
   * Money fields in the request are ignored if sent — **the server prices the
   * order.** A device does not get to decide what a bill costs.
   *
   * `created` distinguishes a fresh 201 from a 200 replay so a screen can tell
   * "sent" from "already sent" without inspecting headers.
   */
  async submitOrder(
    request: SubmitOrderRequest,
    idempotencyKey: string,
    signal?: AbortSignal,
  ): Promise<{order: Order; created: boolean; replayed: boolean}> {
    const {data, status, replayed} = await this.request<Order>({
      method: 'POST',
      path: '/v1/orders',
      body: {...request, channel: CHANNEL},
      idempotencyKey,
      signal,
    });
    return {order: data, created: status === 201, replayed};
  }

  /**
   * `POST /v1/orders/{order_id}/events` — add to a bill that already exists.
   *
   * **This is the second round, and without it a table can order once.** One
   * table carries one bill: `POST /v1/orders` refuses a second live order on the
   * same table with `409 conflict`, and the contract says what to do instead —
   * "a guest phone scanning an occupied table must open that ticket rather than
   * a new one". This is how it opens it.
   *
   * `line_item_added` is **commutative**, which is the property that makes a
   * guest phone safe here: the contract lists it among the events that need no
   * `If-Match`, precisely so "a POS and a guest phone adding to the same table
   * both succeed and both items appear". A guest ordering a second round while a
   * server rings something in at the till cannot lose either.
   *
   * Two idempotency layers, and they answer different questions. The
   * `Idempotency-Key` header replays the whole batch. Each event's own
   * `event_id` deduplicates *that event* — an event already recorded comes back
   * in `duplicate_event_ids` and the call still succeeds, which is what turns a
   * phone's at-least-once retry into effectively-once application.
   *
   * The server prices every line from the channel projection at append time. A
   * device does not decide what a bill costs, here any more than at submit.
   */
  async appendOrderEvents(
    orderId: string,
    events: NewOrderEvent[],
    idempotencyKey: string,
    signal?: AbortSignal,
    ifMatch?: string | null,
  ): Promise<AppendOrderEventsResponse> {
    const {data} = await this.request<AppendOrderEventsResponse>({
      method: 'POST',
      path: `/v1/orders/${encodeURIComponent(orderId)}/events`,
      body: {events},
      idempotencyKey,
      ifMatch,
      signal,
    });
    return data;
  }

  /**
   * `GET /v1/orders/{order_id}` — re-read an order.
   *
   * The read a stream frame is telling you to do. Also the read that makes a
   * confirmation survive a page refresh, which the spec requires because a lost
   * confirmation is a support call.
   */
  async getOrder(orderId: string, signal?: AbortSignal): Promise<Order> {
    const {data} = await this.request<Order>({
      path: `/v1/orders/${encodeURIComponent(orderId)}`,
      signal,
    });
    return data;
  }

  /**
   * `GET /v1/location/service-status` — open, outside hours, or closed.
   *
   * **Never compose this on the client.** A phone in a dining room does not have
   * the merchant's IANA timezone, a correct timezone database, a correct clock
   * or the trading-day cutover rule. The server has one implementation.
   *
   * Returns `null` rather than throwing when the server declines the question —
   * 501 on the contract mock, or 401/403 because the guest override is missing
   * from the `security` block (see the file header). A null verdict means
   * "unknown": show the menu, do not show a closed notice you cannot justify.
   */
  async getServiceStatus(signal?: AbortSignal): Promise<ServiceStatus | null> {
    try {
      const {data} = await this.request<ServiceStatus>({
        path: '/v1/location/service-status',
        signal,
      });
      return data;
    } catch (error) {
      if (
        error instanceof ApiError &&
        (error.code === 'not_implemented' ||
          error.status === 401 ||
          error.status === 403 ||
          error.status === 404)
      ) {
        return null;
      }
      throw error;
    }
  }

  /**
   * `GET /v1/branding` — the merchant's accent colour and logo for this surface.
   *
   * Mirrors `getMenuProjection`: one read, no channel argument, tenant-scoped by
   * the credential. Like `getServiceStatus`, a server that declines the question
   * — 501 on the contract mock, or 401/403/404 on a deployment that has not
   * shipped the route — is turned into `null` rather than an error, because a shop
   * with no branding is the ordinary case and must not break the menu. The caller
   * then keeps the default look, which is exactly what `configured: false` means
   * too, so both routes lead to one place.
   */
  async getBranding(signal?: AbortSignal): Promise<Branding | null> {
    try {
      const {data} = await this.request<Branding>({
        path: '/v1/branding',
        signal,
      });
      return data;
    } catch (error) {
      if (
        error instanceof ApiError &&
        (error.code === 'not_implemented' ||
          error.status === 401 ||
          error.status === 403 ||
          error.status === 404)
      ) {
        return null;
      }
      throw error;
    }
  }

  // --- hosted checkout ------------------------------------------------------

  /**
   * `POST /v1/orders/{order_id}/checkout-sessions` — start a ReservePay hosted
   * checkout and get back the URL to hand the guest to.
   *
   * **This does not take a payment and it does not move money.** It creates a
   * checkout at ReservePay and records the correlation row that is the only copy
   * of the link between their money and this bill. What comes back is a page
   * ReservePay renders; pokpokPOS draws nothing.
   *
   * `idempotencyKey` is **required by the server** and must be minted once per
   * checkout *intent* and reused on every retry — a fresh key on a retry creates
   * a second checkout at ReservePay for one bill, and the guest can pay both. The
   * same key with a **different body** is refused with `idempotency_key_reuse`,
   * so a changed `amount_satang` needs a new key, not the old one.
   *
   * `upstream_timeout` here is not an ordinary failure: it means ReservePay did
   * not answer and a checkout may or may not exist. The contract's instruction is
   * explicit — **do not retry** — and a caller that treats it as retriable is how
   * one order gets two checkouts.
   */
  async createCheckoutSession(
    orderId: string,
    request: CreateCheckoutSessionRequest,
    idempotencyKey: string,
    signal?: AbortSignal,
  ): Promise<{session: CheckoutSession; replayed: boolean}> {
    const {data, replayed} = await this.request<CheckoutSession>({
      method: 'POST',
      path: `/v1/orders/${encodeURIComponent(orderId)}/checkout-sessions`,
      // `return_url` and `preferred_language` are accepted and ignored by the
      // server, so they are not sent. A field that looks implemented and is not
      // costs more than an absent one.
      body: {amount_satang: request.amount_satang},
      idempotencyKey,
      signal,
    });
    return {session: data, replayed};
  }

  /**
   * `GET /v1/checkout-sessions/{checkout_session_id}` — read pokpok's own record.
   *
   * **This does not call ReservePay**, so its `state` changes when pokpok learns
   * something, not when a poll asks. Polling it faster does not make an answer
   * arrive sooner, and today it cannot reach `succeeded` at all — the webhook
   * that would write it stops short of settling. A caller must not build a
   * progress indicator that waits for one.
   */
  async getCheckoutSession(
    checkoutSessionId: string,
    signal?: AbortSignal,
  ): Promise<CheckoutSession> {
    const {data} = await this.request<CheckoutSession>({
      path: `/v1/checkout-sessions/${encodeURIComponent(checkoutSessionId)}`,
      signal,
    });
    return data;
  }

  // --- customer web bookings (guest-token paths) ---------------------------
  //
  // These three are **public**: no guest session token, no scanned QR. The
  // credential model is a `manage_token` the create call serves once, carried in
  // the URL of the read and cancel. `anonymous: true` on all three suppresses the
  // `Authorization` header — a booking link opened cold has no session to send,
  // and sending a stale one from an unrelated sitting would only invite a 401.
  //
  // `tenant_id` travels explicitly (the create body, the read/cancel query)
  // rather than through the client's `X-Pokpok-Tenant-Id` default, because the
  // booking flow resolves its tenant from the link, not from a session — the same
  // way the QR flow reads its tenant from the `m` query parameter, not from state.

  /**
   * `POST /v1/public/bookings` — create a booking.
   *
   * `idempotencyKey` is minted once per submit *intent* and reused on every
   * retry: a fresh key on a double-tap or a retry after a dropped connection is
   * how one guest ends up with two tables held. The header is already on the CORS
   * allow-list, so it costs no preflight.
   *
   * The returned `manage_token` is the only copy of the credential for reading or
   * cancelling this booking later — persist it the instant it arrives.
   */
  async createPublicBooking(
    request: CreatePublicBookingRequest,
    idempotencyKey: string,
    signal?: AbortSignal,
  ): Promise<CreatePublicBookingResponse> {
    const {data} = await this.request<CreatePublicBookingResponse>({
      method: 'POST',
      path: '/v1/public/bookings',
      body: request,
      idempotencyKey,
      anonymous: true,
      signal,
    });
    return data;
  }

  /** `GET /v1/public/bookings/{manage_token}?tenant_id=` — read a booking. */
  async getPublicBooking(
    manageToken: string,
    tenantId: string,
    signal?: AbortSignal,
  ): Promise<Booking> {
    const {data} = await this.request<Booking>({
      path: `/v1/public/bookings/${encodeURIComponent(manageToken)}`,
      query: {tenant_id: tenantId},
      anonymous: true,
      signal,
    });
    return data;
  }

  /**
   * `POST /v1/public/bookings/{manage_token}/cancel?tenant_id=` — cancel.
   *
   * The server enforces a **2-hour-before window**: a cancel inside it, or on an
   * already-terminal booking, is refused with `409`. The screen surfaces that
   * window honestly rather than discovering the refusal after a tap, but the
   * server is the authority and the 409 is handled either way.
   *
   * `idempotencyKey` makes a retry safe: cancelling a booking already cancelled
   * by this same key replays rather than erroring.
   */
  async cancelPublicBooking(
    manageToken: string,
    tenantId: string,
    idempotencyKey: string,
    signal?: AbortSignal,
  ): Promise<Booking> {
    const {data} = await this.request<Booking>({
      method: 'POST',
      path: `/v1/public/bookings/${encodeURIComponent(manageToken)}/cancel`,
      query: {tenant_id: tenantId},
      idempotencyKey,
      anonymous: true,
      signal,
    });
    return data;
  }

  // --- feedback -------------------------------------------------------------

  /**
   * `POST /v1/public/feedback` — a guest leaves a post-bill star rating and an
   * optional comment.
   *
   * ## Authenticated by the guest session token, not anonymous
   *
   * Despite the `public` in the path this is **not** an unauthenticated call —
   * the contract's own `security` block names `guestSessionToken`, the same
   * table-scoped credential every other write on this surface carries. A
   * device/staff token is `403`, an absent one `401`. So this goes out with the
   * ordinary `Authorization` header (no `anonymous` flag) exactly like
   * `submitOrder`.
   *
   * ## The tenant is never in the body
   *
   * "The tenant and location are taken from the guest session token, never the
   * body" — so `FeedbackSubmit` carries no `tenant_id` and this method sends
   * none. A guest cannot address another shop's feed, and passing a tenant here
   * would be the surface pretending it decides which shop is being rated.
   *
   * ## Idempotency, and the one header note
   *
   * `idempotencyKey` is required by the contract on this POST like every other.
   * Mint it once per feedback intent and reuse it on retry, so a phone that
   * never saw the `201` does not record two ratings for one meal — a replay
   * comes back as the original entry.
   *
   * Adds no new header beyond the four already on the CORS allow-list (see the
   * file header): `Authorization`, `Content-Type`, `Idempotency-Key`,
   * `X-Pokpok-Tenant-Id`.
   *
   * PDPA: no name, phone or email is sent — the body is a rating, an optional
   * free-text comment, and at most the bill's `order_id`.
   */
  async submitPublicFeedback(
    feedback: FeedbackSubmit,
    idempotencyKey: string,
    signal?: AbortSignal,
  ): Promise<{feedback: Feedback; replayed: boolean}> {
    const {data, replayed} = await this.request<Feedback>({
      method: 'POST',
      path: '/v1/public/feedback',
      body: feedback,
      idempotencyKey,
      signal,
    });
    return {feedback: data, replayed};
  }
}

function safeParse<T>(text: string): T | undefined {
  if (text.trim() === '') return undefined;
  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined;
  }
}
